"use client";

import { useEffect, useMemo, useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { RiDeleteBinLine } from "react-icons/ri";
import categoryAndGroupAPI from "../../services/categoryAndGroup/categoryAndGroupAPI";
import ConfirmPopUp from "./confirmPopUp";
import StatusPopUp from "./statusPopUp";

type NameInputPopUpProps = {
    title: string;
    onClose: () => void;
    onConfirm?: () => void;
    type: "category" | "group";
};

type ItemRow = {
    id: number | string;
    name: string;
};

function getTeacherUuidFromLocalStorage() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return "";

        const parsed = JSON.parse(raw) as { id?: string };
        if (!parsed || typeof parsed !== "object") return "";

        return typeof parsed.id === "string" ? parsed.id.trim() : "";
    } catch {
        return "";
    }
}

function getRowsFromApiResponse(response: unknown) {
    if (Array.isArray(response)) {
        return response;
    }

    if (Array.isArray((response as { data?: unknown[] })?.data)) {
        return (response as { data: unknown[] }).data;
    }

    return [];
}

export default function GroupOrCategoryPopUp({ title, onClose, onConfirm, type }: NameInputPopUpProps) {
    const [value, setValue] = useState("");
    const [items, setItems] = useState<ItemRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<ItemRow | null>(null);
    const [editTarget, setEditTarget] = useState<ItemRow | null>(null);
    const [editValue, setEditValue] = useState("");
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);

    const itemLabel = useMemo(() => (type === "category" ? "หมวดหมู่" : "กลุ่ม"), [type]);

    const loadItems = async () => {
        setIsLoading(true);
        const teacherUuid = getTeacherUuidFromLocalStorage();
        const response = type === "category"
            ? await categoryAndGroupAPI.getAllCategories()
            : await categoryAndGroupAPI.getGroupsByTeacherUuid(teacherUuid);

        if ((response as { status?: string })?.status === "failed") {
            setItems([]);
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: `โหลด${itemLabel}ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง`,
                type: "error",
            });
            setIsLoading(false);
            return;
        }

        const rows = getRowsFromApiResponse(response);
        const mapped = rows
            .map((row) => {
                const nextId = (row as { id?: number | string })?.id;
                const nextName = String((row as { name?: unknown })?.name ?? "").trim();
                if ((nextId === undefined || nextId === null) || !nextName) return null;
                return {
                    id: nextId,
                    name: nextName,
                };
            })
            .filter(Boolean) as ItemRow[];

        setItems(mapped);
        setIsLoading(false);
    };

    useEffect(() => {
        void loadItems();
    }, [type]);

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, []);

    const handleCreate = async () => {
        const nextValue = value.trim();
        if (!nextValue) {
            setStatusPopUp({
                title: "แจ้งเตือน",
                message: `กรุณากรอกชื่อ${itemLabel}`,
                type: "info",
            });
            return;
        }

        setIsSaving(true);

        const teacherUuid = getTeacherUuidFromLocalStorage();
        const response = type === "category"
            ? await categoryAndGroupAPI.createCategory(nextValue, teacherUuid || undefined)
            : await categoryAndGroupAPI.createGroup(nextValue, teacherUuid || undefined);

        if (response?.status === "failed") {
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: `เพิ่ม${itemLabel}ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง`,
                type: "error",
            });
            setIsSaving(false);
            return;
        }

        setStatusPopUp({
            title: "สำเร็จ",
            message: `เพิ่ม${itemLabel}สำเร็จ`,
            type: "success",
        });
        setValue("");
        await loadItems();
        onConfirm?.();
        setIsSaving(false);
    };

    const handleOpenEdit = (item: ItemRow) => {
        setEditTarget(item);
        setEditValue(item.name);
    };

    const handleCancelEdit = () => {
        setEditTarget(null);
        setEditValue("");
    };

    const handleUpdate = async () => {
        if (!editTarget) return;

        const updatedName = editValue.trim();
        if (!updatedName) {
            setStatusPopUp({
                title: "แจ้งเตือน",
                message: `กรุณากรอกชื่อ${itemLabel}`,
                type: "info",
            });
            return;
        }

        if (updatedName === editTarget.name) {
            handleCancelEdit();
            return;
        }

        setIsUpdating(true);

        const response = type === "category"
            ? await categoryAndGroupAPI.updateCategory(editTarget.id, updatedName)
            : await categoryAndGroupAPI.updateGroup(editTarget.id, updatedName);

        if (response?.status === "failed") {
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: `แก้ไข${itemLabel}ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง`,
                type: "error",
            });
            setIsUpdating(false);
            return;
        }

        setStatusPopUp({
            title: "สำเร็จ",
            message: `แก้ไข${itemLabel}สำเร็จ`,
            type: "success",
        });
        setIsUpdating(false);
        handleCancelEdit();
        await loadItems();
        onConfirm?.();
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;

        const response = type === "category"
            ? await categoryAndGroupAPI.softDeleteCategory(deleteTarget.id)
            : await categoryAndGroupAPI.softDeleteGroup(deleteTarget.id);

        if (response?.status === "failed") {
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: `ลบ${itemLabel}ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง`,
                type: "error",
            });
            return;
        }

        setStatusPopUp({
            title: "สำเร็จ",
            message: `ลบ${itemLabel}สำเร็จ`,
            type: "success",
        });
        setDeleteTarget(null);
        await loadItems();
        onConfirm?.();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
            <div className="w-full max-w-115 rounded-md bg-[#f3f3f3] p-3 shadow-[0_6px_20px_rgba(0,0,0,0.35)]">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold leading-none text-[#4b4b4b]">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-[#d7dade] text-[#8d939a]"
                    >
                        <IoClose size={20} />
                    </button>
                </div>

                <div className="mt-3 flex items-end gap-2">
                    <div className="flex-1">
                        <label className="block text-lg font-semibold text-[#5a5a5a]">ชื่อ{itemLabel}</label>
                        <input
                            type="text"
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                            className="mt-1 h-10 w-full rounded-sm border border-[#d5d5d5] bg-white px-3 text-base text-[#4E4E4E] outline-none"
                            //placeholder={`ใส่ชื่อ${itemLabel}ที่จะบันทึก`}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={isSaving}
                        className="h-10 min-w-20 rounded-md bg-[#1094d4] px-6 text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70 cursor-pointer hover:bg-[#0a9cf0]"
                    >
                        {isSaving ? "..." : "บันทึก"}
                    </button>
                </div>

                <div className="p-1 pt-1 pb-1 text-lg font-semibold text-[#555]">{itemLabel}</div>

                <div className="rounded-sm bg-white px-2 py-1">
                    <div className="max-h-75 overflow-y-auto">
                        {isLoading ? (
                            <div className="py-3 text-sm text-[#777]">กำลังโหลด...</div>
                        ) : items.length === 0 ? (
                            <div className="py-3 text-sm text-[#777]">ไม่พบข้อมูล</div>
                        ) : (
                            items.map((item) => (
                                <div key={item.id} className="flex items-center justify-between py-2 text-lg text-[#4E4E4E]">
                                    <span>{item.name}</span>
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => handleOpenEdit(item)}
                                            className="cursor-pointer text-[#0a9cf0] hover:text-[#0784b9]"
                                            aria-label={`แก้ไข${itemLabel} ${item.name}`}
                                        >
                                            <FiEdit2 size={24} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDeleteTarget(item)}
                                            className="cursor-pointer text-[#ff5b5b] hover:text-[#d43c3c]"
                                            aria-label={`ลบ${itemLabel} ${item.name}`}
                                        >
                                            <RiDeleteBinLine size={24} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {deleteTarget ? (
                <ConfirmPopUp
                    title={`ยืนยันการลบ${itemLabel}`}
                    message={`ต้องการลบ "${deleteTarget.name}" หรือไม่`}
                    cancelText="ยกเลิก"
                    confirmText="ลบ"
                    onCancel={() => setDeleteTarget(null)}
                    onConfirm={() => void handleDelete()}
                />
            ) : null}

            {editTarget ? (
                <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/45 p-4">
                    <div className="w-full max-w-md rounded-md bg-[#f3f3f3] px-5 pb-7 pt-6 shadow-[0_6px_20px_rgba(0,0,0,0.35)]">
                        <div className="mx-auto w-72 rounded-sm border border-[#FDEF5C] bg-[#FDEF5C] py-1 text-center text-lg font-semibold text-[#2B2B2B] shadow-[4px_4px_0_#5d6670]">
                            {`แก้ไข${itemLabel}`}
                        </div>

                        <div className="mt-5">
                            <label className="block text-base font-medium text-[#4E4E4E]">ชื่อ{itemLabel}</label>
                            <input
                                type="text"
                                value={editValue}
                                onChange={(event) => setEditValue(event.target.value)}
                                className="mt-1 h-10 w-full rounded-lg border border-[#bfc2c8] bg-white px-3 text-base outline-none"
                                placeholder={`ใส่ชื่อ${itemLabel}ที่จะบันทึก`}
                            />
                        </div>

                        <div className="mt-6 flex justify-center gap-4">
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                disabled={isUpdating}
                                className="min-w-20 cursor-pointer rounded-md border border-[#c4c4c4] bg-white px-7 py-2 text-base font-semibold text-[#4E4E4E] disabled:cursor-not-allowed disabled:opacity-70"
                            >
                                ยกเลิก
                            </button>
                            <button
                                type="button"
                                onClick={() => void handleUpdate()}
                                disabled={isUpdating}
                                className="min-w-20 cursor-pointer rounded-md bg-[#0a9cf0] px-7 py-2 text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70 hover:bg-[#0784b9]"
                            >
                                {isUpdating ? "กำลังบันทึก..." : "บันทึก"}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={() => setStatusPopUp(null)}
                />
            ) : null}
        </div>
    );
}
